import { useI18n } from '../../i18n';
// ============================================
// EmployeesPage - Main Employee Management Page
// ============================================

import { useState, useMemo } from 'react';
import { PageHeader, PageContainer, ExportButton, showToast } from '../../components/shared';
import { useUsers, useUsersStats, useExport, useDebounce, useCreateUser, useUpdateUser, useDeleteUser, useDeactivateUser, useActivateUser, useStores, useResetUserPassword } from '../../hooks';
import { extractList } from '../../utils/apiList';
import type { UserWithStores } from '@conai/shared-types';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import {
  WorkforceStatCard,
  QuickFiltersCard,
  EmployeeSearchBar,
  EmployeeTable,
  type EmployeeDisplay,
  type DepartmentFilter,
} from './components';

const DEPARTMENT_FILTERS: DepartmentFilter[] = [
  { key: 'warehouse', label: 'Warehouse', count: 42 },
  { key: 'logistics', label: 'Logistics', count: 28 },
  { key: 'sales', label: 'Sales', count: 54 },
];

const JOB_TITLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'STORE_ADMIN', label: 'Store Manager' },
  { value: 'OPERATOR', label: 'Operator' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'AI_ADMIN', label: 'System Admin' },
  { value: 'TECHNICIAN', label: 'Technician' },
  { value: 'POS_TERMINAL', label: 'POS Terminal' },
];

/**
 * Mật khẩu tạm dùng-một-lần khi tạo nhân viên — ngay sau đó FE gọi
 * reset-password để server sinh mật khẩu thật, nên giá trị này không ai
 * dùng để đăng nhập.
 *
 * KHÔNG gọi `crypto.randomUUID()` trần: hàm đó chỉ có trong secure context
 * (HTTPS hoặc localhost). Dev mở qua IP LAN `http://192.168.1.x:3000` là
 * `undefined` → "crypto.randomUUID is not a function" và không tạo được
 * nhân viên nào. `getRandomValues` thì sống ở cả context không an toàn.
 */
function makeTempPassword(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return `Tp1-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  // Đường cùng (môi trường test không có Web Crypto) — vẫn phải đủ dài/ngẫu nhiên.
  return `Tp1-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

// ============================================
// Component
// ============================================

export function EmployeesPage() {
  const { t } = useI18n();
  // State
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 500);
  const [selectedJobTitle, setSelectedJobTitle] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [activeDepartment, setActiveDepartment] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
 
  const jobTitleOptions = useMemo(() => [
    { value: '', label: t('allRoles') },
    { value: 'STORE_ADMIN', label: t('roleStoreAdmin') },
    { value: 'OPERATOR', label: t('roleOperator') },
    { value: 'AI_ADMIN', label: t('roleAiAdmin') },
    { value: 'TECHNICIAN', label: t('roleTechnician') },
    { value: 'POS_TERMINAL', label: t('rolePosTerminal') },
  ], [t]);

  const {
    data: usersData,
    isLoading,
    error,
  } = useUsers({
    search: debouncedSearch || undefined,
    exclude_role: 'CUSTOMER',
    role: selectedJobTitle || undefined,
    is_active: selectedStatus === 'active' ? true : selectedStatus === 'on_leave' ? false : undefined,
    page: currentPage,
    page_size: pageSize,
  } as any);

  const { stats: staffStats } = useUsersStats();

  const employees = useMemo<EmployeeDisplay[]>(
    () =>
      (usersData?.results ?? [])
        .filter((u: UserWithStores) => u.role !== 'CUSTOMER')
        .map((u: UserWithStores) => ({
          id: u.id,
          // Bug #69: mã nhân viên trước đây được BỊA ở client —
          // `EMP-${số thứ tự dòng + offset trang}`. Nó không tồn tại trong dữ
          // liệu, và tệ hơn là ĐỔI theo trang đang xem cùng bộ lọc đang bật:
          // cùng một người ở trang 1 là EMP-0003, lọc lại thành EMP-0001. Không
          // tìm được theo mã là hiển nhiên — chẳng có gì để tìm.
          //
          // Nay đọc mã thật nếu BE có trả; chưa có thì để trống, không bịa.
          // Xem ghi chú gửi BE về việc bổ sung `employee_code`.
          code:
            (u as unknown as { employee_code?: string | null }).employee_code ??
            (u as unknown as { code?: string | null }).code ??
            '-',
          fullName: u.full_name,
          phone: u.phone ?? '-',
          jobTitle: u.role,
          status: u.is_active ? 'active' : 'on_leave',
          startDate: u.created_at,
        })),
    [usersData]
  );

  // Search / role / status filter + pagination are handled server-side.
  const filteredEmployees = employees;

  const paginatedEmployees = filteredEmployees;

  const createEmployee = useCreateUser();
  const updateEmployee = useUpdateUser();
  const deleteEmployee = useDeleteUser();
  const deactivateEmployee = useDeactivateUser();
  const activateEmployee = useActivateUser();

  // Modal States
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);
  const [toggleConfirm, setToggleConfirm] = useState<{ id: string; action: 'activate' | 'deactivate' } | null>(null);
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    role: 'OPERATOR',
  });
  const [formErrors, setFormErrors] = useState<Partial<typeof formData>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Phạm vi cửa hàng của nhân viên (sheet 2 mục #64/#67). `store_ids` đã có
   * sẵn trong Create/UpdateUserRequest — trước đây form không có UI gán nên
   * mọi nhân viên thấy dữ liệu toàn chuỗi. Việc TỰ LỌC dữ liệu theo scope là
   * hành vi BE (dựa JWT), FE chỉ cần gán đúng.
   * Rỗng = không giới hạn (toàn chuỗi) — chỉ nên dùng cho AI_ADMIN.
   */
  const [formStoreIds, setFormStoreIds] = useState<string[]>([]);

  /**
   * Mật khẩu server vừa sinh — hiện MỘT LẦN cho admin chép lại.
   * BE không có đường xem lại, chỉ có reset tiếp; nên không được nhét vào
   * toast tự tắt.
   */
  const [generatedPassword, setGeneratedPassword] = useState<{
    name: string;
    password: string;
  } | null>(null);
  const resetPassword = useResetUserPassword();

  const { data: storesData } = useStores({ page_size: 100 } as never);
  const storeOptions = useMemo(
    () => extractList<{ id: string; name: string }>(storesData).map((s) => ({ id: s.id, name: s.name })),
    [storesData]
  );

  const toggleFormStore = (storeId: string) => {
    setFormStoreIds((prev) =>
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    );
  };

  // Handlers
  const handleDepartmentFilter = (key: string | null) => {
    setActiveDepartment(key);
    if (key === 'warehouse') {
      setSelectedJobTitle('OPERATOR');
    } else if (key === 'logistics') {
      setSelectedJobTitle('TECHNICIAN');
    } else if (key === 'sales') {
      setSelectedJobTitle('STORE_ADMIN');
    } else {
      setSelectedJobTitle('');
    }
    setCurrentPage(1);
  };

  const handleRowAction = (employeeId: string, action: string) => {
    if (action === 'edit') {
      const emp = filteredEmployees.find((e) => e.id === employeeId);
      const originalUser = usersData?.results?.find((u) => u.id === employeeId);
      if (emp) {
        setFormData({
          fullName: emp.fullName,
          phone: (emp.phone === '-' ? '' : emp.phone) || '',
          email: originalUser?.email || '',
          role: originalUser?.role || 'OPERATOR',
        });
        // UserWithStores.stores = phạm vi hiện tại của nhân viên
        setFormStoreIds((originalUser?.stores ?? []).map((s) => s.id));
        setFormErrors({});
        setSubmitError(null);
        setActiveEmployeeId(employeeId);
        setModalMode('edit');
      }
    } else if (action === 'deactivate') {
      setToggleConfirm({ id: employeeId, action: 'deactivate' });
    } else if (action === 'activate') {
      setToggleConfirm({ id: employeeId, action: 'activate' });
    } else if (action === 'reset-password') {
      const emp = filteredEmployees.find((e) => e.id === employeeId);
      resetPassword.mutate(
        { id: employeeId },
        {
          onSuccess: (result) => {
            if (result.password) {
              setGeneratedPassword({
                name: emp?.fullName ?? '',
                password: result.password,
              });
            }
          },
          onError: (err: unknown) =>
            showToast(
              err instanceof Error ? err.message : t('resetPasswordFailed'),
              'error'
            ),
        }
      );
    }
  };

  const openAdd = () => {
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      role: 'OPERATOR',
    });
    setFormStoreIds([]);
    setFormErrors({});
    setSubmitError(null);
    setActiveEmployeeId(null);
    setModalMode('add');
  };

  const closeModal = () => {
    setModalMode(null);
    setActiveEmployeeId(null);
    setSubmitError(null);
    setFormErrors({});
  };

  // Lock background scroll while the Add/Edit Employee modal is open
  useLockBodyScroll(!!modalMode);

  const handleField = (key: keyof typeof formData, value: string) => {
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const handleSave = async () => {
    const errors: Partial<typeof formData> = {};
    if (!formData.fullName.trim()) errors.fullName = t('validationEmployeeNameRequired');
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t('validationInvalidEmailFormat');
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitError(null);
    try {
      if (modalMode === 'add') {
        // Mật khẩu tạm CHỈ tồn tại trong đúng request này: ngay sau khi tạo,
        // FE gọi reset-password để server sinh mật khẩu thật rồi hiện cho admin
        // chép lại (sheet 2 #3/#63). Trước đây chỗ này hardcode 'Password123!'
        // và không có đường đổi — mọi nhân viên mới dùng chung một mật khẩu ai
        // đọc code cũng biết.
        const created = await createEmployee.mutateAsync({
          full_name: formData.fullName.trim(),
          phone: formData.phone.trim() || undefined,
          email: formData.email.trim() || '',
          role: formData.role as any,
          password: makeTempPassword(),
          store_ids: formStoreIds,
        });

        const result = await resetPassword.mutateAsync({ id: created.id });
        if (result.password) {
          setGeneratedPassword({ name: created.full_name, password: result.password });
        }
      } else if (modalMode === 'edit' && activeEmployeeId) {
        await updateEmployee.mutateAsync({
          id: activeEmployeeId,
          data: {
            full_name: formData.fullName.trim(),
            phone: formData.phone.trim() || undefined,
            email: formData.email.trim() || undefined,
            role: formData.role as any,
            store_ids: formStoreIds,
          },
        });
      }
      closeModal();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('failedToSaveEmployee'));
    }
  };

  const handleConfirmToggle = async () => {
    if (!toggleConfirm) return;
    try {
      if (toggleConfirm.action === 'deactivate') {
        await deactivateEmployee.mutateAsync(toggleConfirm.id);
      } else {
        await activateEmployee.mutateAsync(toggleConfirm.id);
      }
      setToggleConfirm(null);
    } catch (err) {
      setToggleConfirm(null);
    }
  };

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending;
  // Bug #68: một cờ duy nhất cho "đang gửi request đổi trạng thái" — hộp thoại
  // dùng nó để khoá cả ba đường đóng, không chỉ nút xác nhận.
  const isTogglePending = deactivateEmployee.isPending || activateEmployee.isPending;

  const { triggerExport, isExporting, progress: exportProgress } = useExport({ endpoint: '/users/', filePrefix: 'employees' });

  const inputCls = "h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none";

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        title={t('employees')}
        subtitle={t('manageWorkforceAccessRolesAndDepart')}
        actions={
          <div className="flex items-center gap-2">
            <ExportButton onExport={triggerExport} isLoading={isExporting} progress={exportProgress} />
            <button
              onClick={openAdd}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors"
              style={{
                background: 'linear-gradient(165deg, #004AC6, #2563EB)',
                boxShadow: '0px 4px 12px rgba(37, 99, 235, 0.3)',
              }}
            >
              {t('addEmployeeBtn')}
            </button>
          </div>
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadEmployees')}</p>
          <p className="mt-1 text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while loading employees.'}
          </p>
        </div>
      )}

      {/* Bento Dashboard */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <WorkforceStatCard
          total={staffStats.active}
          deltaPercent={staffStats.deltaPercent}
          deltaDirection={(staffStats.deltaPercent ?? 0) >= 0 ? 'up' : 'down'}
          deltaLabel={t('increaseThisMonth')}
          isLoading={isLoading}
          className="lg:col-span-8"
        />
        <QuickFiltersCard
          filters={useMemo(() => ([
            { key: 'warehouse', label: t('roleOperator'), count: staffStats.byRole['OPERATOR'] ?? 0 },
            { key: 'logistics', label: t('roleTechnician'), count: staffStats.byRole['TECHNICIAN'] ?? 0 },
            { key: 'sales', label: t('roleStoreAdmin'), count: staffStats.byRole['STORE_ADMIN'] ?? 0 },
          ]), [staffStats.byRole, t])}
          activeKey={activeDepartment}
          onFilter={handleDepartmentFilter}
          className="lg:col-span-4"
        />
      </div>

      {/* Search & Filter Toolbar */}
      <div className="mt-8">
        <EmployeeSearchBar
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            setCurrentPage(1);
          }}
          selectedJobTitle={selectedJobTitle}
          onJobTitleChange={(value) => {
            setSelectedJobTitle(value);
            if (value === 'OPERATOR') {
              setActiveDepartment('warehouse');
            } else if (value === 'TECHNICIAN') {
              setActiveDepartment('logistics');
            } else if (value === 'STORE_ADMIN') {
              setActiveDepartment('sales');
            } else {
              setActiveDepartment(null);
            }
            setCurrentPage(1);
          }}
          selectedStatus={selectedStatus}
          onStatusChange={(value) => {
            setSelectedStatus(value);
            setCurrentPage(1);
          }}
          jobTitleOptions={jobTitleOptions}
        />
      </div>

      {/* Employee Table */}
      <div className="mt-6">
        <EmployeeTable
          data={paginatedEmployees}
          totalCount={usersData?.count ?? 0}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onRowAction={handleRowAction}
          isLoading={isLoading}
        />
      </div>

      {/* Add/Edit Modal.
          Bug #68 (reviewer bắt): cùng bệnh với modal toggle bên dưới — đang
          create/update mà backdrop, nút X, Cancel vẫn đóng được, trông như đã
          huỷ trong khi request vẫn chạy. Khoá cả ba đường khi isSubmitting. */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => { if (!isSubmitting) closeModal(); }}>
          <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">{modalMode === 'add' ? t('addEmployee') : t('editEmployee')}</h3>
              <button onClick={closeModal} disabled={isSubmitting} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors disabled:cursor-not-allowed disabled:opacity-40">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 px-6 pb-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('fullNameLabel')} <span className="text-red-500">*</span></label>
                  <input value={formData.fullName} onChange={(e) => handleField('fullName', e.target.value)} placeholder={t('enterFullNamePlaceholder')} className={`${inputCls} ${formErrors.fullName ? 'border-red-300 bg-red-50' : ''}`} />
                  {formErrors.fullName && <p className="mt-1 text-xs text-red-600">{formErrors.fullName}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('phone')}</label>
                  <input value={formData.phone} onChange={(e) => handleField('phone', e.target.value)} placeholder={t('enterPhonePlaceholder')} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('email')}</label>
                <input value={formData.email} onChange={(e) => handleField('email', e.target.value)} placeholder={t('enterEmailPlaceholder')} className={`${inputCls} ${formErrors.email ? 'border-red-300 bg-red-50' : ''}`} />
                {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('role')}</label>
                <select value={formData.role} onChange={(e) => handleField('role', e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-400 focus:outline-none">
                  {jobTitleOptions.filter(opt => opt.value !== '').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Phạm vi cửa hàng (mục #64/#67): dữ liệu nhân viên thấy sẽ bị
                  BE lọc theo danh sách này. Không tick gì = toàn chuỗi. */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('storeScopeLabel')}</label>
                <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                  {storeOptions.length === 0 ? (
                    <p className="px-1 py-1 text-xs text-slate-400">{t('storeScopeLoading')}</p>
                  ) : (
                    storeOptions.map((store) => (
                      <label key={store.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm text-slate-700 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={formStoreIds.includes(store.id)}
                          onChange={() => toggleFormStore(store.id)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="truncate">{store.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-400">{t('storeScopeHint')}</p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
              {submitError && <p className="mr-auto text-xs text-red-600">{submitError}</p>}
              <button onClick={closeModal} disabled={isSubmitting} className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:cursor-not-allowed disabled:opacity-40">{t('cancel')}</button>
              <button onClick={handleSave} disabled={isSubmitting} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {isSubmitting ? t('savingBtn') : t('saveBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Status Confirm.
          Bug #68: khi đang gửi request, KHOÁ mọi đường đóng hộp thoại (nền, nút
          X, nút Cancel). Trước đây cả ba đều gọi thẳng `setToggleConfirm(null)`
          không xét trạng thái, nên đóng hộp thoại giữa chừng trông như đã huỷ —
          trong khi request đã rời trình duyệt và server vẫn deactivate như
          thường. Đóng được nhưng không huỷ được là kiểu giao diện nói dối.

          Không dùng AbortController ở đây: huỷ request phía client KHÔNG huỷ
          được thao tác server đã nhận, nên nó cũng chỉ là một lời nói dối khác,
          tinh vi hơn. Cách trung thực là không cho đóng cho tới khi có kết quả. */}
      {/* Mật khẩu vừa sinh — hiện MỘT LẦN.
          Cố ý KHÔNG dùng toast: BE không có đường xem lại (chỉ có reset tiếp),
          nên một thông báo tự tắt sau vài giây là mất mật khẩu của nhân viên
          vừa tạo. Hộp thoại chỉ đóng khi người dùng chủ động bấm. */}
      {generatedPassword && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[440px] rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900">{t('passwordGeneratedTitle')}</h3>
            <p className="mt-1 text-sm text-slate-600">
              {t('passwordGeneratedBody', { name: generatedPassword.name })}
            </p>

            <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <code className="flex-1 select-all break-all font-mono text-sm text-slate-900">
                {generatedPassword.password}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(generatedPassword.password);
                  showToast(t('passwordCopied'), 'success');
                }}
                className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                {t('copy')}
              </button>
            </div>

            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('passwordGeneratedWarning')}
            </p>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setGeneratedPassword(null)}
                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {t('passwordSavedConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}
          onClick={() => { if (!isTogglePending) setToggleConfirm(null); }}
        >
          <div className="w-full max-w-[440px] rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">
                {toggleConfirm.action === 'deactivate' ? t('deactivateEmployee') : t('activateEmployee')}
              </h3>
              <button
                onClick={() => setToggleConfirm(null)}
                disabled={isTogglePending}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100" />
            <div className="px-6 py-5">
              <p className="text-sm text-slate-500">
                {toggleConfirm.action === 'deactivate'
                  ? t('confirmDeactivateEmployee')
                  : t('confirmActivateEmployee')}
              </p>
              {isTogglePending && (
                <p className="mt-3 text-xs text-slate-400">{t('actionInProgressCannotCancel')}</p>
              )}
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <button
                onClick={() => setToggleConfirm(null)}
                disabled={isTogglePending}
                className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleConfirmToggle}
                disabled={isTogglePending}
                className={`rounded-lg px-5 py-2 text-sm font-semibold text-white disabled:opacity-60 transition-colors ${
                  toggleConfirm.action === 'deactivate' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {isTogglePending
                  ? t('processing')
                  : toggleConfirm.action === 'deactivate'
                    ? t('deactivate')
                    : t('activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
